import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ShoppingCart, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const EUR_RATE = 55.85;

const DURATION_MAP = {
  "شهري": "Monthly",
  "سنة": "1 Year",
  "سنتين": "2 Years",
  "3 سنوات": "3 Years",
  "4 سنوات": "4 Years",
  "5 سنوات": "5 Years"
};

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const productId = searchParams.get("product");
  const cycle = searchParams.get("cycle") || "monthly";

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: ""
  });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/api/products`);
        const selectedProduct = response.data.find(p => p.id === productId);
        setProduct(selectedProduct);
      } catch (error) {
        toast.error("Failed to load product");
      } finally {
        setLoading(false);
      }
    };
    if (productId) fetchProduct();
  }, [productId]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && typeof event.data === "object") {
        const { paymentStatus } = event.data;
        if (paymentStatus === "SUCCESS" || paymentStatus === "success") {
          navigate("/payment-success");
        }
      }
      if (event.data && typeof event.data === "string") {
        try {
          const data = JSON.parse(event.data);
          if (data.paymentStatus === "SUCCESS" || data.paymentStatus === "success") {
            navigate("/payment-success");
          }
        } catch(e) {}
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const orderData = {
        ...formData,
        product_id: product.id,
        product_name: product.name_en || product.name_ar,
        amount: priceEgp,
        plan_duration: cycle
      };
      const orderResponse = await axios.post(`${API}/api/orders`, orderData);
      const orderId = orderResponse.data.order_id;

      const paymentResponse = await axios.post(`${API}/api/payment/initiate`, {
        order_id: orderId,
        amount: priceEgp,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email
      });

      if (paymentResponse.data.status === "success") {
        setPaymentUrl(paymentResponse.data.iframe_url);
      } else {
        toast.error("Error initiating payment");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error creating order");
    } finally {
      setSubmitting(false);
    }
  };

  const priceEgp = product ? (product.price_total || 0) : 0;
  const priceEur = product ? (product.price_eur || Math.ceil(priceEgp / EUR_RATE)) : 0;
  const durationEn = product ? (DURATION_MAP[product.duration] || (cycle === "monthly" ? "Monthly" : "Yearly")) : "";

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center">
        <div className="text-white text-xl">Product not found</div>
      </div>
    );
  }

  if (paymentUrl) {
    window.location.href = paymentUrl;
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-500 mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-white">Redirecting to payment...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950" dir="ltr">
      <Navbar />
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="max-w-5xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-8 text-center">Checkout</h1>
            <div className="grid md:grid-cols-3 gap-8">
              {/* Order Summary */}
              <div className="md:col-span-1">
                <Card className="bg-white/5 border-white/10 sticky top-24">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <ShoppingCart className="h-5 w-5" />
                      Order Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">{product.name_en || product.name_ar}</h3>
                      <p className="text-sm text-gray-300">{product.description_en || product.description_ar}</p>
                    </div>
                    <div className="border-t border-white/10 pt-4">
                      <Badge className="bg-red-500/20 text-red-300 border-red-500/30">
                        {durationEn}
                        {durationEn !== "Monthly" && " — One-time"}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-white font-semibold">Features:</h4>
                      <ul className="space-y-1">
                        {product.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                            <CheckCircle className="h-4 w-4 text-green-400" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="border-t border-white/10 pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">Total:</span>
                        <span className="text-2xl font-bold text-white">€{priceEur}</span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1 text-right">
                        {priceEgp.toLocaleString()} EGP
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Form */}
              <div className="md:col-span-2">
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Customer Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="customer_name" className="text-white">Full Name *</Label>
                        <Input id="customer_name" name="customer_name" value={formData.customer_name} onChange={handleChange} required className="bg-white/10 border-white/20 text-white placeholder:text-gray-400" placeholder="Enter your full name" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="customer_email" className="text-white">Email *</Label>
                        <Input id="customer_email" name="customer_email" type="email" value={formData.customer_email} onChange={handleChange} required className="bg-white/10 border-white/20 text-white placeholder:text-gray-400" placeholder="example@email.com" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="customer_phone" className="text-white">Phone *</Label>
                        <Input id="customer_phone" name="customer_phone" type="tel" value={formData.customer_phone} onChange={handleChange} required className="bg-white/10 border-white/20 text-white placeholder:text-gray-400" placeholder="+20 11 42796664" />
                      </div>
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <p className="text-sm text-red-300">💳 You will be redirected to Kashier secure payment page to complete your payment.</p>
                      </div>
                      <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-black font-semibold py-6">
                        {submitting ? "Processing..." : `Proceed to Payment — €${priceEur}`}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CheckoutPage;
